import { Node } from "@jsplumbtoolkit/browser-ui";
import { RandomId } from "@swivl-mono/helpers";
import { NodeType } from "@swivl-mono/types";

export const OFFSET_INCREMENT = 50; // pixels

/**
 * Generates a unique node ID that doesn't exist in the provided list
 */
export function generateUniqueNodeId(existingIds: string[]): string {
  let newId: string;
  do {
    newId = RandomId(6);
  } while (existingIds.includes(newId));
  return newId;
}

/**
 * Calculates a safe position for a duplicate node that doesn't collide with existing nodes
 */
export function calculateSafePosition(originalNode: Node, existingNodes: Node[], multiplier: number = 1): { left: number; top: number } {
  const baseLeft = originalNode.data.left + OFFSET_INCREMENT * multiplier;
  const baseTop = originalNode.data.top + OFFSET_INCREMENT * multiplier;

  // Check for exact position collision with existing nodes
  const hasCollision = existingNodes.some((node) => node.data.left === baseLeft && node.data.top === baseTop);

  if (hasCollision) {
    return calculateSafePosition(originalNode, existingNodes, multiplier + 1);
  }

  return { left: baseLeft, top: baseTop };
}

/**
 * Deep clones a node's data while preserving all properties
 */
export function cloneNodeData(node: Node): any {
  // Deep clone the node data
  const clonedData = JSON.parse(JSON.stringify(node.data));

  // Ensure the cloned data maintains its type
  if (!clonedData.type) {
    clonedData.type = node.data.type;
  }

  return clonedData;
}

/**
 * Checks if a node is a start node
 */
export function isStartNode(node: Node): boolean {
  return node.data.type === NodeType.Start || node.type === "start";
}

/**
 * Filters out start nodes from a list of nodes
 */
export function filterOutStartNodes(nodes: Node[]): Node[] {
  return nodes.filter((node) => !isStartNode(node));
}

/**
 * Creates a mapping between original node IDs and their duplicate IDs
 */
export function createIdMapping(originalNodes: Node[], duplicateNodes: Node[]): Map<string, string> {
  const mapping = new Map<string, string>();
  originalNodes.forEach((original, index) => {
    if (duplicateNodes[index]) {
      mapping.set(original.id, duplicateNodes[index].id);
    }
  });
  return mapping;
}

/**
 * Updates gotos in duplicated nodes based on ID mapping
 */
export function updateGotosForDuplicates(nodes: any[], idMapping: Map<string, string>, clearExternalGotos: boolean = false): any[] {
  return nodes.map((node) => {
    const updatedNode = { ...node };

    // Comprehensive list of all goto properties found across the codebase
    const gotoProperties = [
      // Basic goto properties
      "goto",
      "successGoto",
      "errorGoto",
      "onSaveGoto",
      "onCompleteGoto",
      "onMissingParamsGoto",
      "hasNotGoto",
      "greaterThanGoto",
      "lessThanGoto",
      "equalToGoto",
      "onPickedGoto",
      "onlyItemAutomaticallyPickedGoto",
      "noItemsGoto",
      "noLeasesGoto",
      "onErrorGoto",
      "storeFoundGoto",
      "storeNotSelectedGoto",
      "missingParamsGoto",
      "invalidGoto",
      "machineAnsweredGoto",
      "noAnswerGoto",
      "timeoutGoto",
      "conditionMetGoto",
      "conditionNotMetGoto",

      // Validation and routing properties
      "validGoto",
      "ifInvalidGoto",
      "invalidPhoneNumberGoto",
      "invalidEmailGoto",
      "newUserGoto",
      "returningUserGoto",

      // SpacePicker node properties
      "selectedGoto",
      "notSelectedGoto",
      "searchDifferentStoreGoto",
      "noUnitsGoto",

      // ProfileCollect node properties
      "savedGoto",
      "canceledGoto",
      "freeTextGoto",

      // SMS/Phone related properties
      "noPhoneNumberGoto",

      // TableSaveRow properties
      "onMissingRequiredFieldGoto",

      // StoreGetTenant node properties
      "multipleTenantsGoto",
      "notFoundGoto",

      // DateAndTime node properties
      "withinTimeGoto",
      "elseGoto",

      // Array node properties
      "notSetGoto"
    ];

    gotoProperties.forEach((prop) => {
      if (updatedNode[prop]) {
        const originalGoto = updatedNode[prop];
        const newId = idMapping.get(originalGoto);
        if (newId) {
          updatedNode[prop] = newId;
        } else if (clearExternalGotos) {
          // Clear gotos that point outside the duplicated set
          delete updatedNode[prop];
        }
      }
    });

    // Handle buttons/actions with gotos (comprehensive)
    if (updatedNode.actions && Array.isArray(updatedNode.actions)) {
      updatedNode.actions.forEach((action: any) => {
        // Handle onSelectGoto (existing)
        if (action.onSelectGoto) {
          const newId = idMapping.get(action.onSelectGoto);
          if (newId) {
            action.onSelectGoto = newId;
          } else if (clearExternalGotos) {
            delete action.onSelectGoto;
          }
        }

        // Handle goto (MultiSelect, Buttons nodes)
        if (action.goto) {
          const newId = idMapping.get(action.goto);
          if (newId) {
            action.goto = newId;
          } else if (clearExternalGotos) {
            delete action.goto;
          }
        }
      });
    }

    // Handle node conditions with multiple goto types (Logic, URL nodes)
    if (updatedNode.conditions && Array.isArray(updatedNode.conditions)) {
      updatedNode.conditions.forEach((condition: any) => {
        // Handle Logic node conditionMetGoto
        if (condition.conditionMetGoto) {
          const newId = idMapping.get(condition.conditionMetGoto);
          if (newId) {
            condition.conditionMetGoto = newId;
          } else if (clearExternalGotos) {
            condition.conditionMetGoto = null; // Logic nodes use null instead of undefined
          }
        }

        // Handle URL node conditions with goto
        if (condition.goto) {
          const newId = idMapping.get(condition.goto);
          if (newId) {
            condition.goto = newId;
          } else if (clearExternalGotos) {
            delete condition.goto;
          }
        }
      });
    }

    // Handle Action node outputs with onCompleteGoto
    if (updatedNode.outputs && Array.isArray(updatedNode.outputs)) {
      updatedNode.outputs.forEach((output: any) => {
        if (output.onCompleteGoto) {
          const newId = idMapping.get(output.onCompleteGoto);
          if (newId) {
            output.onCompleteGoto = newId;
          } else if (clearExternalGotos) {
            delete output.onCompleteGoto; // Clear invalid external gotos
          }
        }
      });
    }

    // Handle Random node paths with onCompleteGoto
    if (updatedNode.paths && Array.isArray(updatedNode.paths)) {
      updatedNode.paths.forEach((path: any) => {
        if (path.onCompleteGoto) {
          const newId = idMapping.get(path.onCompleteGoto);
          if (newId) {
            path.onCompleteGoto = newId;
          } else if (clearExternalGotos) {
            delete path.onCompleteGoto;
          }
        }
      });
    }

    // Handle RouterStart node returningAfter with goto
    if (updatedNode.returningAfter && Array.isArray(updatedNode.returningAfter)) {
      updatedNode.returningAfter.forEach((item: any) => {
        if (item.goto) {
          const newId = idMapping.get(item.goto);
          if (newId) {
            item.goto = newId;
          } else if (clearExternalGotos) {
            delete item.goto;
          }
        }
      });
    }

    // Handle unhandledOutput for certain node types
    if (updatedNode.unhandledOutput && updatedNode.unhandledOutput.goto) {
      const newId = idMapping.get(updatedNode.unhandledOutput.goto);
      if (newId) {
        updatedNode.unhandledOutput.goto = newId;
      } else if (clearExternalGotos) {
        delete updatedNode.unhandledOutput.goto;
      }
    }

    return updatedNode;
  });
}
